import { useState, useRef, useEffect, useMemo } from 'react';

/**
 * FlowAssistant — Asistente conversacional moderno para FlowexPay.
 *
 * Diseño 2025: avatar con gradiente, typing indicators, input de texto
 * con búsqueda inteligente, animaciones suaves, personalización.
 *
 * Capacidades:
 * - Pre-canned responses con fuzzy matching de keywords
 * - Quick replies contextuales
 * - Personalización con nombre del usuario
 * - Estado persistido en localStorage
 * - Handoff preparado a Alex (soporte real)
 */

// Base de conocimiento — keywords detectan intent, respuestas son conversacionales
const KNOWLEDGE_BASE = [
  // Seguridad: celular perdido / robo / bloqueo (SIEMPRE primero — es una emergencia)
  {
    id: 'celular-perdido',
    keywords: [
      'perdi mi celular', 'perdí mi celular', 'me robaron', 'robaron mi celular', 'robo del celular',
      'perdi el telefono', 'perdí el teléfono', 'celular perdido', 'telefono perdido', 'teléfono perdido',
      'bloquear mi cuenta', 'bloquear cuenta', 'bloqueen mi cuenta', 'congelar mi cuenta',
      'congela mi dinero', 'congelar mi dinero', 'me hackearon', 'hackearon', 'cuenta comprometida',
      'acceso no autorizado', 'desbloquear mi cuenta', 'desbloquear cuenta', 'emergencia',
    ],
    intent: 'Perdí mi celular / bloquear mi cuenta',
    response: [
      { type: 'text', content: '🚨 Si perdiste tu celular o crees que alguien entró a tu cuenta, lo primero es **bloquear tu dinero — es inmediato y gratis**.' },
      { type: 'text', content: '1️⃣ Escríbenos al **WhatsApp de soporte de FlowexPay** desde CUALQUIER número (el de un familiar sirve), o al correo de soporte\n2️⃣ Di "quiero bloquear mi cuenta" e indica el **correo de tu cuenta FlowexPay**\n3️⃣ Congelamos TODA salida de dinero al instante' },
      { type: 'text', content: 'Tu plata y tus inversiones quedan **intactas** — solo se congela la salida. Nadie puede enviar ni retirar nada, ni siquiera desde tu celular.' },
      { type: 'text', content: 'Para **desbloquear** te pedimos una **videollamada con tu documento en mano** (verificación de identidad). Nunca desbloqueamos solo por chat — esa fricción es la que te protege.' },
    ],
    quickReplies: ['Contactar a soporte', 'Otra pregunta'],
  },
  // FlowexPay Business (dashboard empresarial)
  {
    id: 'flowexpay-business',
    keywords: [
      'empresa', 'empresarial', 'mi negocio', 'nomina', 'nómina', 'pagar empleados', 'empleados',
      'business', 'pagos masivos', 'dispersion', 'dispersión', 'proveedores', 'tesoreria', 'tesorería',
      'pagar la nomina', 'rrhh', 'recursos humanos', 'aprobar pagos', 'cuenta de empresa',
      'cuenta empresarial', 'pyme', 'compania', 'compañia', 'sas', 'nit',
    ],
    intent: 'FlowexPay para mi empresa',
    response: [
      { type: 'text', content: '**FlowexPay Business** es la cuenta de tu empresa: su plata, sus pagos y su tesorería en un solo lugar.' },
      { type: 'text', content: 'Lo mejor: la **nómina con control total** — tu equipo (RRHH) prepara los pagos con una plantilla, y **solo tú los apruebas con un código en tu celular**. Nadie mueve dinero sin ti. Todo queda auditado y con factura DIAN automática.' },
      { type: 'text', content: 'Las empresas entran por la pestaña **"Empresas"** al iniciar sesión, y llegan a su propio dashboard con su Oráculo de tesorería.' },
      { type: 'text', content: 'Está en **piloto privado**: si quieres vincular tu empresa, escríbenos por **Contactar a soporte** y el equipo FlowexPay la configura contigo con verificación previa.' },
    ],
    quickReplies: ['Como recargo', 'Enviar al mundo', 'Otra pregunta'],
  },
  // Recargas
  {
    id: 'recargar-bank',
    keywords: [
      // Verbos conjugados
      'recarg', 'cargar', 'cargo', 'carga', 'depositar', 'deposit', 'meter', 'mete', 'metí', 'pongo',
      'añadir', 'agregar', 'agreg', 'ingresar', 'ingres', 'fondear', 'fondeo', 'subir saldo',
      // Sustantivos LATAM
      'plata', 'lana', 'biyas', 'biuyuyo', 'dinero', 'saldo', 'fondos', 'efectivo',
      // Metodos específicos
      'pse', 'banco colombia', 'davivienda', 'bancolombia', 'bbva', 'nequi', 'daviplata',
      // Frases comunes
      'meter plata', 'meter dinero', 'subir plata', 'cómo cargo', 'cómo deposito',
      'desde mi banco', 'cuenta bancaria', 'transferencia bancaria', 'recibir dinero',
      'entrar plata', 'mandar a mi cuenta', 'cargar saldo', 'tener saldo',
    ],
    intent: 'Recargar desde mi banco',
    response: [
      { type: 'text', content: 'Para recargar tu cuenta desde tu banco es muy fácil:' },
      { type: 'text', content: '1️⃣ Ve a **Inicio → Recargar**\n2️⃣ Selecciona **PSE** (si estás en Colombia)\n3️⃣ Elige tu banco y monto\n4️⃣ Te redirige al portal de tu banco para autorizar' },
      { type: 'text', content: 'El dinero llega a tu cuenta FlowexPay en minutos y se convierte automáticamente a USDC.' },
      { type: 'text', content: 'Si estás fuera de Colombia, también puedes usar tus **Cuentas Globales** para recibir vía ACH (USA), SEPA (Europa), PIX (Brasil) o SPEI (México). Esos métodos van por el menú de **Cuentas Globales**.' },
    ],
    quickReplies: ['Recargar con crypto', 'Cuentas Globales', 'Otra pregunta'],
  },
  {
    id: 'recargar-crypto',
    keywords: [
      'cripto', 'crypto', 'usdc', 'usdt', 'btc', 'bitcoin', 'eth', 'ethereum', 'sol', 'solana',
      'wallet', 'billetera', 'metamask', 'phantom', 'binance', 'coinbase', 'kraken',
      'criptomoneda', 'criptos', 'token', 'tokens', 'stablecoin', 'estable',
      'desde otra wallet', 'desde binance', 'desde exchange', 'red blockchain', 'polygon',
      'blockchain', 'red solana', 'que red', 'cual red', 'que token', 'que cripto',
    ],
    intent: 'Recargar con crypto',
    response: [
      { type: 'text', content: 'Para recargar con crypto:' },
      { type: 'text', content: '1️⃣ Ve a **Inicio → Recargar**\n2️⃣ Selecciona **Blockchain**\n3️⃣ Copia tu dirección de wallet FlowexPay\n4️⃣ Envía USDC desde tu exchange o wallet externo' },
      { type: 'text', content: '⚠️ **IMPORTANTE**:\n• Red: **Solana** únicamente\n• Token: solo **USDC** (por ahora)\n• Si envías por otra red o con otro token, los fondos se pierden' },
      { type: 'text', content: 'Llega en segundos una vez confirmada la transacción en Solana.' },
      { type: 'text', content: '🔜 Próximamente habilitaremos USDT, BTC, SOL y HYPE vía Fireblocks.' },
    ],
    quickReplies: ['Recargar con banco (PSE)', 'Cómo enviar USDC', 'Otra pregunta'],
  },
  // Enviar
  {
    id: 'enviar-bank',
    keywords: [
      'enviar', 'envío', 'envio', 'enviá', 'envia', 'enviar', 'envíame', 'envialo',
      'mandar', 'mando', 'manda', 'mandé', 'mande', 'mándame',
      'retirar', 'retiro', 'retira', 'retiré', 'retirando',
      'sacar', 'saco', 'saca', 'saqué', 'sacando', 'salida',
      'transferir', 'transfiero', 'transfiere', 'transferí', 'transferí',
      'girar', 'giro', 'gira', 'giré', 'remesa', 'remesar',
      'pagar', 'pago', 'pagué', 'pagar',
      'off ramp', 'off-ramp', 'salida fiat', 'sacar plata',
      'mandar a banco', 'enviar a banco', 'a mi cuenta', 'a otra cuenta',
      'a un amigo', 'a familia', 'a colombia', 'a usa', 'a mexico', 'a brasil',
    ],
    intent: 'Enviar a cuenta bancaria',
    response: [
      { type: 'text', content: 'Para enviar a una cuenta bancaria:' },
      { type: 'text', content: '1️⃣ Ve a **Enviar**\n2️⃣ Registra la cuenta destino (solo primera vez)\n3️⃣ Define el monto en USDC\n4️⃣ Confirma — el receptor recibe en moneda local' },
      { type: 'text', content: '⏱️ **Tiempos de llegada:**' },
      { type: 'text', content: '🇨🇴 **Colombia**\n• Bancolombia y Nequi: 15 min - 1 hora\n• Otros bancos (ACH): ~4 horas\n🇺🇸 **USA**\n• ACH: siguiente día hábil, $0 comisión\n• Wire: mismo día, $20 USD\n🇲🇽 🇧🇷 🇪🇺 **México, Brasil, Europa**: mismo día (1-4 horas)' },
    ],
    quickReplies: ['Enviar a otro cliente FlowexPay', 'Enviar a crypto wallet', 'Otra pregunta'],
  },
  {
    id: 'enviar-amigo',
    keywords: ['enviar amigo', 'p2p', 'otro cliente', 'flowexpay user', 'entre clientes', 'gratis', 'sin comision', 'comision cero', 'a un amigo'],
    intent: 'Enviar a otro cliente FlowexPay',
    response: [
      { type: 'text', content: '✨ Esto es uno de los mejores beneficios:' },
      { type: 'text', content: 'Enviar entre clientes de FlowexPay es:\n⚡ **Inmediato**\n💵 **$0 de comisión**\n🌎 Sin límite de monto\n🔒 Solo necesitas el **correo registrado** del receptor' },
      { type: 'text', content: 'Es la forma más eficiente de mandar plata entre familia y amigos que también usen FlowexPay.' },
    ],
    quickReplies: ['Enviar a banco', 'Enviar a crypto wallet', 'Otra pregunta'],
  },
  {
    id: 'enviar-crypto',
    keywords: [
      'enviar crypto', 'enviar cripto', 'enviar usdc', 'mandar usdc', 'transferir usdc',
      'a wallet', 'a billetera', 'a otra wallet', 'wallet externo',
      'red solana', 'red polygon', 'que red enviar', 'que red usar', 'enviar a binance',
    ],
    intent: 'Enviar a wallet crypto',
    response: [
      { type: 'text', content: 'Para enviar USDC a un wallet externo:' },
      { type: 'text', content: '1️⃣ Ve a **Enviar → Wallet crypto**\n2️⃣ Pega la dirección del wallet destino\n3️⃣ Selecciona la **red**: Solana o Polygon\n4️⃣ Define el monto y confirma' },
      { type: 'text', content: '💵 **Costo**: 0.5% sobre el monto\n⚡ **Tiempo**: minutos\n🌐 **Redes disponibles**: Solana y Polygon' },
      { type: 'text', content: '⚠️ Importante: revisa que la dirección y la red sean correctas. Los envíos crypto son **irreversibles**.' },
    ],
    quickReplies: ['Enviar a otro cliente FlowexPay', 'Enviar a banco', 'Otra pregunta'],
  },
  // Metas con Proposito
  {
    id: 'metas',
    keywords: [
      'meta', 'metas', 'mis metas', 'ahorro', 'ahorrar', 'ahorro con proposito', 'proposito', 'propósito',
      'invertir con proposito', 'objetivo', 'sueño', 'sueno', 'plan de ahorro', 'meta de ahorro',
      'casa', 'cuota inicial', 'viaje', 'estudios', 'carro', 'tu dinero ahorra contigo', 'ahorrar para',
      'juntar plata', 'reunir plata', 'comprar casa', 'comprar carro', 'pagar viaje',
    ],
    intent: '¿Qué son las Metas?',
    response: [
      { type: 'text', content: '🎯 **Mis Metas** es para invertir con propósito: defines un sueño (una casa, un viaje, estudios) y te ayudamos a llegar.' },
      { type: 'text', content: '**Cómo funciona:**\n1️⃣ Ve a **Mis Metas → Crear meta**\n2️⃣ Pon el nombre, el monto en pesos y la fecha\n3️⃣ Te decimos cuánto ahorrar al mes y qué portafolio te conviene' },
      { type: 'text', content: '✨ **Lo más bonito**: no ahorras solo. Al invertir tu dinero, **el mercado también suma a tu meta**. La barra muestra en dos colores cuánto pusiste tú y cuánto sumó el mercado, y te dice cuántos **días te adelantaste**.' },
      { type: 'text', content: '💸 **Aportar e invertir**: cuando aportas, compramos tu portafolio automáticamente (repartido en los activos correctos) con verificación por SMS. Y el **Oráculo** te hace seguimiento: pregúntale "¿cómo van mis metas?".' },
    ],
    quickReplies: ['¿Cuánto puedo ahorrar?', '¿Qué es Mi Plan?', 'Otra pregunta'],
  },
  {
    id: 'diagnostico-ahorro',
    keywords: [
      'cuanto puedo ahorrar', 'cuánto puedo ahorrar', 'cuanto ahorrar', 'cuanto deberia ahorrar',
      'diagnostico', 'diagnóstico', 'no se cuanto ahorrar', 'presupuesto', 'finanzas personales',
      'cuanto me queda', 'cuanto me sobra', 'ayuda a ahorrar', 'no me alcanza', 'organizar mi plata',
    ],
    intent: '¿Cuánto puedo ahorrar?',
    response: [
      { type: 'text', content: 'Si no sabes cuánto ahorrar, tenemos un diagnóstico de **3 preguntas**, sin enredos.' },
      { type: 'text', content: '**Cómo:**\n1️⃣ Ve a **Mis Metas → ¿Cuánto puedo ahorrar?**\n2️⃣ Responde: cuánto te entra, cuánto gastas en lo fijo, cuánto en gustos\n3️⃣ Te decimos cuánto podrías ahorrar **cómodo** cada mes (sin apretarte)' },
      { type: 'text', content: 'Y con ese número, creas tu meta de una. Pensado para que cualquiera lo entienda, sin saber de finanzas.' },
    ],
    quickReplies: ['¿Qué son las Metas?', 'Comprar mi primera acción', 'Otra pregunta'],
  },
  // Invertir
  {
    id: 'invertir-oraculo',
    keywords: ['oraculo', 'oráculo', 'analisis', 'análisis', 'que es oraculo', 'cerebro', 'ia', 'inteligencia artificial', 'recomendacion'],
    intent: '¿Qué es El Oráculo?',
    response: [
      { type: 'text', content: 'El Oráculo 🔮 es el cerebro de inversión de FlowexPay.' },
      { type: 'text', content: 'A diferencia de otros brokers que solo te muestran el precio de la acción, El Oráculo te da tesis completas de cada empresa: qué hace, moat, hacia dónde va, riesgos, escenarios futuros.' },
      { type: 'text', content: 'La profundidad depende de tu nivel:\n🥉 Básico — resumen simple\n🥈 Intermedio — fundamentales\n🥇 Avanzado — técnico + riesgo\n👑 Premium — tesis institucional' },
      { type: 'text', content: '🎧 **Bonus**: El Oráculo ahora también te lo NARRA en voz alta. Al abrir cualquier acción ves un reproductor arriba con el audio del tier actual. Perfecto si prefieres escuchar mientras haces otras cosas.' },
      { type: 'text', content: 'Lo encuentras al entrar a cualquier acción en **Invertir → Mercado**.' },
    ],
    quickReplies: ['¿Cómo escucho el análisis?', '¿Qué es Mi Plan?', 'Cómo comprar mi primera acción'],
  },
  {
    id: 'audio-oraculo',
    keywords: [
      'audio', 'escuchar', 'escucho', 'oir', 'oír', 'narra', 'narrar', 'narrado', 'narracion', 'narración',
      'voz', 'sarah', 'podcast', 'reproductor', 'play', 'leer en voz alta', 'no me gusta leer',
      'dame el audio', 'cómo escucho', 'hay audio', 'reproducir', 'sonido',
    ],
    intent: '¿Cómo escucho el análisis del Oráculo?',
    response: [
      { type: 'text', content: 'Sí 🎧 El Oráculo te narra cada análisis con voz humana real.' },
      { type: 'text', content: '**Cómo escucharlo:**\n1️⃣ Ve a **Invertir → Mercado**\n2️⃣ Click en cualquier acción (AAPL, NVDA, etc.)\n3️⃣ Verás un reproductor con fondo azul oscuro arriba del análisis\n4️⃣ Click ▶ Play' },
      { type: 'text', content: '⏱️ **Duración según tu nivel**:\n🥉 Básico — ~45 segundos\n🥈 Intermedio — ~1:30 min\n🥇 Avanzado — ~3 minutos\n👑 Premium — ~5 minutos (tesis institucional completa)' },
      { type: 'text', content: '🎁 **Tu portafolio asignado** (Mi Plan) tiene audio premium completo SIEMPRE, sin importar tu nivel. Es ~5 minutos con la filosofía, riesgos y bear case completo — para que entiendas perfectamente en qué estás invirtiendo.' },
    ],
    quickReplies: ['¿Qué es Mi Plan?', '¿Qué es el glosario?', '¿Qué es El Oráculo?'],
  },
  {
    id: 'glosario',
    keywords: [
      'glosario', 'glossary', 'que significa', 'no entiendo', 'palabra', 'palabras',
      'definicion', 'definición', 'terminologia', 'terminología', 'jerga', 'subrayado',
      'subrayadas', 'subrayado punteado', 'palabra azul', 'click en palabra', 'concepto',
      'que es moat', 'que es ebitda', 'que es p/e', 'que es etf', 'que es dividendo',
      'que es apy', 'que es vault', 'que es defi', 'que es usdc', 'que es accion',
      'explicame', 'que significa esta palabra', 'no se que es', 'que es eso',
    ],
    intent: '¿Qué es el glosario?',
    response: [
      { type: 'text', content: 'En el Oráculo verás algunas palabras con **subrayado punteado** (como un guion bajo). Esas son palabras del glosario 📖' },
      { type: 'text', content: '**Cómo usarlo:**\n1️⃣ Click en cualquier palabra subrayada\n2️⃣ Se abre un popup con:\n   • Definición en 2 frases sin jerga\n   • Ejemplo con una empresa que conoces\n   • Botón ▶ Play para escucharlo (~10 seg)' },
      { type: 'text', content: '🎯 **Lo que cubre**: 80 términos curados — desde lo más básico (acción, ETF, dividendo) hasta lo más sofisticado (moat, EBITDA, data flywheel) pasando por cripto (USDC, vault, APY) y temas colombianos (DIAN, FIC, retención en la fuente).' },
      { type: 'text', content: '💡 **Tip**: cada palabra se subraya solo la primera vez que aparece en el texto para no saturarte visualmente.' },
    ],
    quickReplies: ['¿Cómo escucho el análisis?', '¿Qué es El Oráculo?', '¿Qué es Mi Plan?'],
  },
  {
    id: 'invertir-plan',
    keywords: ['mi plan', 'portafolio', 'plan personalizado', 'que es mi plan', '7 preguntas', 'cuestionario', 'visionario', 'explorador', 'guardian', 'income builder'],
    intent: '¿Qué es Mi Plan?',
    response: [
      { type: 'text', content: 'Mi Plan es el portafolio personalizado que te asignamos según las 7 preguntas del onboarding.' },
      { type: 'text', content: 'Hay 5 perfiles:\n🛡️ **Guardian** — preservar capital\n🧭 **Explorer** — balance crecimiento/seguridad\n🚀 **Visionary** — apuesta a innovación\n💰 **Income Builder** — dividendos crecientes\n🎨 **Custom** — armas el tuyo' },
      { type: 'text', content: 'Cada plan incluye una tesis personalizada, composición sugerida, y análisis específico de El Oráculo.' },
      { type: 'text', content: '🎧 **Lo más importante**: tu portafolio tiene un **audio premium completo** (~5 minutos) narrado por el Oráculo, sin importar tu nivel. Incluye filosofía, apuestas centrales, comportamiento macro, pre-mortem honesto y bear case. Escúchalo antes de invertir — es la mejor forma de tomar una decisión con criterio.' },
      { type: 'text', content: 'Lo ves en **Invertir → Mi Plan**.' },
    ],
    quickReplies: ['¿Cómo escucho mi plan?', 'Mostrar El Oráculo', 'Comprar mi primera acción'],
  },
  {
    id: 'primera-compra',
    keywords: ['comprar', 'mi primera', 'como compro', 'invertir', 'accion', 'acciones', 'stocks', 'comprar acciones', 'cuanto invertir'],
    intent: 'Comprar mi primera acción',
    response: [
      { type: 'text', content: 'Te lo explico paso a paso:' },
      { type: 'text', content: '1️⃣ Ve a **Invertir → Mercado**\n2️⃣ Busca la acción (ej: AAPL, NVDA, NU)\n3️⃣ Lee el análisis del **Oráculo** 🔮\n4️⃣ Escribe el monto en USD\n5️⃣ Click **"Comprar"**' },
      { type: 'text', content: '💵 **Costos**:\n• Comisión por transacción: **$2 USD**\n• Acciones fraccionadas: sí (no necesitas comprar 1 acción completa)' },
      { type: 'text', content: '💡 **Recomendación**: invierte mínimo **$50 USD por transacción** para que la comisión represente menos del 4% del monto. Si inviertes más (ej: $100+), el costo relativo baja a 2% o menos.' },
      { type: 'text', content: 'El monto sale de tu saldo USDC. Si no tienes suficiente, te llevamos a recargar primero.' },
    ],
    quickReplies: ['Recargar mi cuenta', '¿Qué es El Oráculo?', 'Otra pregunta'],
  },
  // Términos
  {
    id: 'cartera-vs-portafolio',
    keywords: ['cartera', 'portafolio', 'diferencia', 'mi cartera', 'mi portafolio', 'cartera vs portafolio', 'mi dinero', 'que es mi dinero'],
    intent: 'Mi dinero vs Mi Portafolio',
    response: [
      { type: 'text', content: 'Buena pregunta — son cosas distintas:' },
      { type: 'text', content: '💼 **Mi dinero** = tu saldo disponible (dólares digitales).\nDesde ahí recargas, conviertes y envías. (Antes se llamaba "Mi Cartera".)' },
      { type: 'text', content: '📈 **Mi Portafolio** = tus acciones y ETFs invertidos.\nApple, NVIDIA, VOO, etc. — patrimonio que sube o baja con el mercado.' },
      { type: 'text', content: 'Resumen: **Mi dinero** = tu plata disponible. **Portafolio** = tu plata invertida en bolsa.' },
    ],
    quickReplies: ['¿Qué son Cuentas Globales?', '¿Qué son los Rendimientos?', 'Otra pregunta'],
  },
  {
    id: 'cuentas-globales',
    keywords: ['cuentas globales', 'cuenta virtual', 'cuenta usa', 'cuenta europa', 'recibir como local', 'on ramp', 'iban', 'ach', 'pix', 'spei'],
    intent: '¿Qué son las Cuentas Globales?',
    response: [
      { type: 'text', content: 'Son cuentas bancarias virtuales en otros países que te damos **sin costo**.' },
      { type: 'text', content: 'Funcionan en doble vía:\n📥 **Recibir** USD/EUR/BRL/MXN como si fueras local de ese país\n📤 **Enviar** desde tu cuenta de FlowexPay a cuentas locales en esos países' },
      { type: 'text', content: '🌎 **Países disponibles hoy**:\n🇺🇸 USA · 🇪🇺 Europa · 🇧🇷 Brasil · 🇲🇽 México' },
      { type: 'text', content: '✅ **Cómo solicitarlas**:\n1️⃣ Ve a la pestaña **Cuentas Globales**\n2️⃣ Click en el **link azul "Solicitar cuenta"**\n3️⃣ Selecciona el país (USA, Brasil, México, Europa)\n4️⃣ Se crea en **menos de 2 minutos**' },
      { type: 'text', content: '👁️ **Cómo ver los datos**: dentro de Cuentas Globales, click en los **tres puntos** de cada cuenta. Ahí ves los datos para recibir transferencias o para enviar dinero a esas cuentas locales.' },
    ],
    quickReplies: ['¿Cómo recargo desde estas cuentas?', '¿Por qué son útiles?', 'Otra pregunta'],
  },
  {
    id: 'generar-rendimiento',
    keywords: ['rendimiento', 'rendimientos', 'generar rendimiento', 'kamino', 'defi', 'yield', 'ganar intereses', 'que es rendimiento', 'recompensas', 'ahorro', 'apy'],
    intent: '¿Qué son los Rendimientos?',
    response: [
      { type: 'text', content: 'Tu dinero puede estar quieto, o puede trabajar para ti 24/7. La sección **Rendimientos** es para eso.' },
      { type: 'text', content: 'FlowexPay te conecta directamente con **protocolos DeFi descentralizados** para que tu USDC genere rendimiento.' },
      { type: 'text', content: '📌 **Cómo funciona legalmente**:\nFlowexPay NO capta, custodia ni administra recursos. Solo es un **intermediario tecnológico** que facilita tu conexión con el protocolo. Tú mantienes la **titularidad y control total** de tus criptoactivos en todo momento.' },
      { type: 'text', content: '🌱 **Características**:\n• Tasa: la que el protocolo DeFi tenga en cada momento (variable)\n• Sin períodos de bloqueo — puedes retirar cuando quieras\n• Sin compromisos ni penalidades' },
      { type: 'text', content: '⚠️ **Importante**: el rendimiento NO está garantizado por FlowexPay. Es determinado por el protocolo DeFi y varía con el mercado. El uso de DeFi conlleva riesgos asumidos por el usuario.' },
    ],
    quickReplies: ['¿Qué es Mi dinero?', '¿Es seguro?', 'Otra pregunta'],
  },
  // Historial
  {
    id: 'historial',
    keywords: ['historial', 'movimientos', 'transacciones', 'ver movimientos', 'comprobante', 'extracto'],
    intent: 'Historial y movimientos',
    response: [
      { type: 'text', content: 'En **Historial** ves TODOS tus movimientos:' },
      { type: 'text', content: '📥 Recargas\n📤 Envíos\n📈 Compras de acciones\n🌱 Rendimientos\n💱 Conversiones' },
      { type: 'text', content: 'Cada movimiento tiene fecha, monto, estado, y comprobante descargable en PDF.' },
      { type: 'text', content: '¿Quieres saber cómo descargar un comprobante específico?' },
    ],
    quickReplies: ['Cómo descargar comprobante', 'Filtrar por fecha', 'Otra pregunta'],
  },
  // Soporte
  {
    id: 'soporte',
    keywords: ['ayuda', 'soporte', 'humano', 'alex', 'problema', 'error', 'no funciona', 'hablar con alguien', 'contactar', 'whatsapp'],
    intent: 'Hablar con Alex (soporte real)',
    response: [
      { type: 'text', content: 'Para casos específicos, **Alex** es nuestro asistente de soporte real en WhatsApp.' },
      { type: 'text', content: 'Alex puede:\n💬 Resolver problemas de transacciones\n💬 Ayudarte con KYC y documentos\n💬 Asistir con dudas legales o fiscales\n💬 Conectarte con un humano si lo necesitas' },
      { type: 'text', content: 'Toca el botón abajo para abrir WhatsApp y hablar con él directamente ⬇️' },
    ],
    quickReplies: ['📱 Abrir WhatsApp con Alex', 'Otra pregunta'],
  },
  {
    id: 'seguridad',
    keywords: ['seguro', 'seguridad', 'custodia', 'fireblocks', 'alpaca', 'regulado', 'sec', 'donde guardan'],
    intent: '¿Qué tan seguros están mis fondos?',
    response: [
      { type: 'text', content: 'Tu seguridad es nuestra prioridad #1. Te explico:' },
      { type: 'text', content: '🔐 **USDC** custodiado en Fireblocks (MPC top-tier institucional)\n🔐 **Stocks** custodiados en Alpaca Securities (regulado SEC, FINRA)\n🔐 **Cuentas Globales** en bancos regulados por país\n🔐 **KYC/AML** cumplimos regulación colombiana' },
      { type: 'text', content: '⚠️ Importante: nunca compartas tu contraseña ni el código de verificación con nadie. Nosotros nunca te lo pedimos.' },
    ],
    quickReplies: ['Otra pregunta', 'Hablar con Alex'],
  },
  {
    id: 'referidos',
    keywords: ['referido', 'referidos', 'invitar', 'invita', 'codigo', 'compartir', 'amigo', 'amigos', 'embajador', 'influencer', 'leyenda', 'comision gratis', 'sin comision', 'gana', 'invita y gana', 'referral'],
    intent: '¿Cómo funciona Invita y Gana?',
    response: [
      { type: 'text', content: '**Invita y Gana** es nuestro programa de referidos. Invitas amigos, y ambos ganan.' },
      { type: 'text', content: '**¿Cómo funciona?**\n\n1️⃣ Comparte tu código de invitación (lo encuentras en la sección **Logros**)\n2️⃣ Tu amigo se registra con tu código\n3️⃣ Cuando tu amigo hace su primera inversión, ambos reciben recompensas' },
      { type: 'text', content: '**¿Qué ganan?**\n\n🎁 **Tu amigo** — Su primera compra es sin comisión\n🎁 **Tú** — Tu siguiente compra es sin comisión' },
      { type: 'text', content: '**Niveles de recompensa:**\n\n🥉 **Embajador** (1 amigo) — 1 compra sin comisión\n⭐ **Influencer** (5 amigos) — 5 compras sin comisión\n👑 **Leyenda** (10 amigos) — $20 USD invertidos gratis en tu portafolio' },
      { type: 'text', content: 'Solo se cuenta como referido exitoso cuando tu amigo hace su primera inversión real.' },
    ],
    quickReplies: ['¿Cómo recargo?', '¿Cómo invertir?', 'Otra pregunta'],
  },
];

// Sugerencias iniciales de quick reply
const INITIAL_SUGGESTIONS = [
  '¿Qué son las Metas?',
  '¿Cuánto puedo ahorrar?',
  '¿Qué es El Oráculo?',
  '¿Cómo recargo mi cuenta?',
  '¿Cómo funciona Invita y Gana?',
];

// Alex — soporte real en WhatsApp
const ALEX_WHATSAPP_NUMBER = '573204088142';
function openAlexWhatsApp(context = '') {
  const baseMessage = 'Hola Alex 👋 Estoy usando la app de FlowexPay y necesito ayuda';
  const fullMessage = context ? `${baseMessage} con: ${context}` : baseMessage + '.';
  const url = `https://wa.me/${ALEX_WHATSAPP_NUMBER}?text=${encodeURIComponent(fullMessage)}`;
  window.open(url, '_blank');
}

// Sugerencias por defecto si no hay match
const FALLBACK_SUGGESTIONS = [
  '¿Cómo recargar?',
  '¿Cómo invertir?',
  '📱 Abrir WhatsApp con Alex',
];

// Normaliza texto: lowercase, sin tildes, sin caracteres especiales
function normalize(text) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // Remueve tildes
    .replace(/[¿?¡!.,;:()'"]/g, ' ')  // Remueve puntuación
    .replace(/\s+/g, ' ')
    .trim();
}

// Stopwords en español que no aportan al matching
const STOPWORDS = new Set([
  'el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas',
  'de', 'del', 'al', 'a', 'en', 'con', 'por', 'para', 'sin',
  'que', 'cual', 'cuales', 'como', 'cuando', 'donde', 'porque',
  'es', 'son', 'soy', 'eres', 'fue', 'era', 'sea',
  'mi', 'mis', 'tu', 'tus', 'su', 'sus', 'me', 'te', 'se',
  'yo', 'tu', 'el', 'ella', 'nosotros', 'ustedes', 'ellos',
  'y', 'o', 'pero', 'si', 'no', 'lo', 'le', 'les',
  'hay', 'esto', 'eso', 'esta', 'ese', 'aqui', 'alli',
  'puedo', 'puede', 'pueden', 'quiero', 'quieres',
]);

function findBestMatch(input) {
  if (!input || input.trim().length < 2) return null;

  const normalized = normalize(input);
  const words = normalized.split(' ').filter(w => w.length >= 3 && !STOPWORDS.has(w));

  if (words.length === 0) return null;

  // Score cada entry
  const scored = KNOWLEDGE_BASE.map((entry) => {
    let score = 0;
    const normalizedKeywords = entry.keywords.map(normalize);
    const normalizedIntent = normalize(entry.intent);

    // 1. Match exacto de keywords (peso alto)
    for (const kw of normalizedKeywords) {
      if (normalized.includes(kw)) {
        score += kw.length * 3; // Peso 3x
      }
    }

    // 2. Match parcial de cada palabra del input contra keywords (peso medio)
    // Esto es lo que arregla "recargo" → "recarg"
    for (const word of words) {
      for (const kw of normalizedKeywords) {
        // Si el keyword es raíz de la palabra (ej: 'recarg' incluido en 'recargo')
        if (word.includes(kw) && kw.length >= 3) {
          score += kw.length * 2; // Peso 2x
        }
        // O si la palabra del input es raíz del keyword
        if (kw.includes(word) && word.length >= 4) {
          score += word.length;
        }
      }
    }

    // 3. Match en intent (peso medio)
    for (const word of words) {
      if (normalizedIntent.includes(word)) {
        score += word.length;
      }
    }

    // 4. Match en contenido de respuestas (peso bajo)
    if (entry.response) {
      const allContent = entry.response.map(r => normalize(r.content || '')).join(' ');
      for (const word of words) {
        if (allContent.includes(word)) {
          score += word.length * 0.3; // Peso 0.3x
        }
      }
    }

    return { entry, score };
  }).filter((x) => x.score > 0);

  if (scored.length === 0) return null;
  scored.sort((a, b) => b.score - a.score);

  // Si el mejor match tiene score muy bajo, mejor devolver null para mostrar fallback
  if (scored[0].score < 4) return null;

  return scored[0].entry;
}

function getUserName() {
  try {
    const profile = localStorage.getItem('fp_investor_profile');
    if (profile) {
      const p = JSON.parse(profile);
      if (p.email) return p.email.split('@')[0].split('.')[0];
    }
    const user = localStorage.getItem('fp_user');
    if (user) {
      const u = JSON.parse(user);
      if (u.firstName) return u.firstName;
      if (u.name) return u.name.split(' ')[0];
    }
  } catch {}
  return null;
}

function FlowAvatar({ size = 'w-9 h-9' }) {
  return (
    <div className={`${size} relative flex-shrink-0`}>
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #EC4899 100%)',
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center text-white">
        <svg width="60%" height="60%" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"
            fill="white"
            stroke="white"
            strokeWidth="0.5"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      {/* Online dot */}
      <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-white" />
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-4 py-3 bg-white border border-[#E5E7EB] rounded-2xl rounded-tl-sm w-fit">
      <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  );
}

function MessageBubble({ msg, isFirst, isLast }) {
  const isUser = msg.from === 'user';

  // Render markdown bold + newlines
  const renderContent = (text) => {
    const lines = text.split('\n');
    return lines.map((line, lineIdx) => {
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      return (
        <div key={lineIdx} className={lineIdx > 0 ? 'mt-1' : ''}>
          {parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
            }
            return <span key={i}>{part}</span>;
          })}
        </div>
      );
    });
  };

  return (
    <div className={`flex mb-1.5 ${isUser ? 'justify-end' : 'justify-start'} animate-slide-up`}>
      {!isUser && isFirst && (
        <div className="mr-2 mt-auto">
          <FlowAvatar size="w-7 h-7" />
        </div>
      )}
      {!isUser && !isFirst && <div className="w-9 flex-shrink-0" />}
      <div
        className={`max-w-[78%] px-3.5 py-2.5 text-sm leading-relaxed ${
          isUser
            ? 'bg-[#2A59C6] text-white rounded-2xl rounded-tr-sm'
            : 'bg-white border border-[#E5E7EB] text-[#181D27] rounded-2xl rounded-tl-sm'
        }`}
      >
        {renderContent(msg.content)}
      </div>
    </div>
  );
}

export default function FlowAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [input, setInput] = useState('');
  const [quickReplies, setQuickReplies] = useState(INITIAL_SUGGESTIONS);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const userName = useMemo(() => getUserName(), []);
  const sessionId = useMemo(() => 'web-' + Math.random().toString(36).slice(2, 10), []);

  // Initial greeting
  useEffect(() => {
    if (open && messages.length === 0) {
      const greeting = userName
        ? `¡Hola ${userName.charAt(0).toUpperCase() + userName.slice(1)}! 👋`
        : '¡Hola! 👋';
      setTyping(true);
      setTimeout(() => {
        setMessages([
          { from: 'flow', content: greeting, ts: Date.now() },
        ]);
        setTyping(false);
        setTimeout(() => {
          setTyping(true);
          setTimeout(() => {
            setMessages((m) => [
              ...m,
              {
                from: 'flow',
                content: 'Soy la ayuda de FlowexPay. Te oriento con la app: recargas, envíos, y dudas de tu cuenta. Para invertir, convertir o enviar tu dinero, eso lo hace el Oráculo por ti — dímelo y te llevo.',
                ts: Date.now(),
              },
            ]);
            setTyping(false);
            // Tip 3era burbuja: anuncia las features nuevas (audio + glosario)
            setTimeout(() => {
              setTyping(true);
              setTimeout(() => {
                setMessages((m) => [
                  ...m,
                  {
                    from: 'flow',
                    content: '💡 **Nuevo**: ya puedes crear **Metas** (invertir con propósito). Defines un sueño, te decimos cuánto ahorrar, y al invertir **el mercado también suma a tu meta**. Míralo en **Mis Metas** del menú. Si no sabes cuánto ahorrar, prueba "¿Cuánto puedo ahorrar?".',
                    ts: Date.now(),
                  },
                ]);
                setTyping(false);
              }, 1100);
            }, 600);
          }, 900);
        }, 400);
      }, 600);
    }
  }, [open]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  const FLOW_CHAT_URL = '/flow-api/chat';

  // Cerebro de Flow (mismo cerebro y tono de Alex). Conversa de verdad.
  // Si el cerebro no responde, cae al respaldo local por palabras clave.
  const handleSend = async (text) => {
    const userText = (text || input).trim();
    if (!userText) return;

    setMessages((m) => [...m, { from: 'user', content: userText, ts: Date.now() }]);
    setInput('');
    setQuickReplies([]);
    setTyping(true);

    const historial = messages
      .filter((x) => x.from === 'user' || x.from === 'flow')
      .slice(-10)
      .map((x) => ({
        role: x.from === 'user' ? 'user' : 'assistant',
        content: typeof x.content === 'string' ? x.content : '',
      }))
      .filter((x) => x.content);

    try {
      const res = await fetch(FLOW_CHAT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mensaje: userText, historial, session_id: sessionId, nombre: userName || '', token: localStorage.getItem('token') || '' }),
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      if (!data || !data.respuesta) throw new Error('sin respuesta');
      setTyping(false);
      setMessages((m) => [...m, { from: 'flow', content: data.respuesta, ts: Date.now() }]);
      setQuickReplies(INITIAL_SUGGESTIONS);
    } catch (e) {
      localFallback(userText);
    }
  };

  // Respaldo local por palabras clave (si el cerebro no está disponible).
  const localFallback = (userText) => {
    setTyping(true);

    // Process after delay (more natural)
    setTimeout(() => {
      const match = findBestMatch(userText);
      if (match) {
        // Add responses sequentially for natural feel
        const responses = match.response;
        let delay = 0;
        responses.forEach((r, i) => {
          setTimeout(() => {
            if (i === 0) setTyping(false);
            setMessages((m) => [...m, { from: 'flow', content: r.content, ts: Date.now() + i }]);
            if (i === responses.length - 1) {
              setQuickReplies(match.quickReplies || INITIAL_SUGGESTIONS);
            }
          }, delay);
          delay += 600;
        });
      } else {
        // Fallback inteligente: detecta tema general
        setTimeout(() => {
          setTyping(false);
          const lowerInput = userText.toLowerCase();

          // Heurísticas de tema
          let topicHint = '';
          let suggestedReplies = FALLBACK_SUGGESTIONS;

          if (/meta|ahorr|proposit|propósit|sueñ|sueno|objetivo|cuota inicial|juntar|reunir/.test(lowerInput)) {
            topicHint = '¿Quieres **ahorrar con propósito** (Mis Metas)?';
            suggestedReplies = [
              '¿Qué son las Metas?',
              '¿Cuánto puedo ahorrar?',
              '¿Qué es Mi Plan?',
            ];
          } else if (/recarg|carg|deposit|met|fond|plat|saldo/.test(lowerInput)) {
            topicHint = '¿Buscas información sobre **recargar tu cuenta**?';
            suggestedReplies = [
              'Recargar desde mi banco',
              'Recargar con crypto',
              'Recibir de otra persona',
            ];
          } else if (/env|mand|retir|sac|transfer|gir|pag|salid/.test(lowerInput)) {
            topicHint = '¿Quieres **enviar o retirar dinero**?';
            suggestedReplies = [
              'Enviar a banco',
              'Enviar a otro cliente',
              'Enviar a crypto wallet',
            ];
          } else if (/inver|accion|stock|bols|cripto|comprar|vender/.test(lowerInput)) {
            topicHint = '¿Te interesa **invertir o las acciones**?';
            suggestedReplies = [
              '¿Qué es El Oráculo?',
              'Comprar mi primera acción',
              '¿Qué es Mi Plan?',
            ];
          } else if (/cuent|globa|virtua|usa|euro|brasil|mexico/.test(lowerInput)) {
            topicHint = '¿Preguntas sobre **Cuentas Globales**?';
            suggestedReplies = [
              '¿Qué son las Cuentas Globales?',
              'Recargar desde mi banco',
              '📱 Abrir WhatsApp con Alex',
            ];
          } else if (/rendim|gana|interes|kamino|yield|defi|recompens/.test(lowerInput)) {
            topicHint = '¿Sobre los **Rendimientos**?';
            suggestedReplies = [
              '¿Qué son los Rendimientos?',
              '¿Qué es Mi dinero?',
              '📱 Abrir WhatsApp con Alex',
            ];
          } else if (/proble|error|ayud|sopor|funcion|fall/.test(lowerInput)) {
            topicHint = 'Parece que tienes un **problema específico**. Alex puede ayudarte mejor:';
            suggestedReplies = [
              '📱 Abrir WhatsApp con Alex',
              'Volver al inicio',
            ];
          } else {
            topicHint = 'No estoy seguro de entenderte 🤔';
            suggestedReplies = FALLBACK_SUGGESTIONS;
          }

          setMessages((m) => [
            ...m,
            {
              from: 'flow',
              content: topicHint,
              ts: Date.now(),
            },
            {
              from: 'flow',
              content: 'Aquí hay algunas opciones que pueden ayudarte 👇',
              ts: Date.now() + 1,
            },
          ]);
          setQuickReplies(suggestedReplies);
        }, 700);
      }
    }, 700);
  };

  const handleQuickReply = (reply) => {
    // Reset shortcut
    if (reply === 'Volver al inicio') {
      handleReset();
      return;
    }
    // Botones que abren WhatsApp con Alex
    if (reply === '📱 Abrir WhatsApp con Alex' || reply === 'Hablar con Alex') {
      // Detectar contexto: última pregunta del usuario
      const lastUserMsg = [...messages].reverse().find((m) => m.from === 'user');
      const context = lastUserMsg ? lastUserMsg.content : '';
      openAlexWhatsApp(context);
      // Mensaje de confirmación
      setMessages((m) => [
        ...m,
        { from: 'user', content: reply, ts: Date.now() },
        {
          from: 'flow',
          content: '¡Listo! Abrí WhatsApp para que hables con Alex 📱',
          ts: Date.now() + 1,
        },
        {
          from: 'flow',
          content: 'Si quieres seguir explorando aquí, dime en qué más te puedo ayudar.',
          ts: Date.now() + 2,
        },
      ]);
      setQuickReplies(INITIAL_SUGGESTIONS);
      return;
    }
    handleSend(reply);
  };

  const handleReset = () => {
    setMessages([]);
    setQuickReplies(INITIAL_SUGGESTIONS);
    setInput('');
  };

  return (
    <>
      {/* Custom CSS for animations */}
      <style>{`
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.25s ease-out;
        }
        @keyframes float-in {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-float-in {
          animation: float-in 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes pulse-soft {
          0%, 100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4); }
          50% { box-shadow: 0 0 0 8px rgba(99, 102, 241, 0); }
        }
        .pulse-soft {
          animation: pulse-soft 2s ease-out infinite;
        }
      `}</style>

      {/* Floating button — bottom-right (Alex está en WhatsApp, no hay conflicto) */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[9998] flex items-center gap-2.5 pl-2 pr-2 sm:pr-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 bg-white border border-[#E5E7EB]"
          aria-label="Abrir ayuda"
        >
          <FlowAvatar size="w-9 h-9" />
          <div className="hidden sm:block text-left">
            <div className="text-xs font-bold text-[#181D27] leading-tight">Ayuda</div>
            <div className="text-[10px] text-[#6B7280] leading-tight">Soporte FlowexPay</div>
          </div>
        </button>
      )}

      {/* Chat panel — bottom-right */}
      {open && (
        <div className="fixed bottom-6 right-6 z-[9998] w-[380px] max-w-[calc(100vw-3rem)] h-[620px] max-h-[calc(100vh-3rem)] bg-white rounded-2xl shadow-2xl border border-[#E5E7EB] flex flex-col overflow-hidden animate-float-in">
          {/* Header */}
          <div className="relative px-4 py-3.5 flex items-center justify-between flex-shrink-0 border-b border-[#F3F4F6]" style={{
            background: 'linear-gradient(135deg, #FAFBFF 0%, #F0F4FF 100%)',
          }}>
            <div className="flex items-center gap-3">
              <FlowAvatar size="w-10 h-10" />
              <div>
                <div className="text-sm font-bold text-[#181D27] flex items-center gap-1.5">
                  Ayuda
                </div>
                <div className="text-[10px] text-[#10B981] flex items-center gap-1 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                  Soporte FlowexPay
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleReset}
                className="text-[#6B7280] hover:text-[#181D27] p-1.5 rounded-lg hover:bg-white/60"
                aria-label="Nueva conversación"
                title="Nueva conversación"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 11-3-6.7L21 3" />
                  <polyline points="21 3 21 9 15 9" />
                </svg>
              </button>
              <button
                onClick={() => setOpen(false)}
                className="text-[#6B7280] hover:text-[#181D27] p-1.5 rounded-lg hover:bg-white/60"
                aria-label="Minimizar"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 bg-[#FAFBFF]">
            {messages.map((msg, i) => {
              const prev = messages[i - 1];
              const next = messages[i + 1];
              const isFirst = !prev || prev.from !== msg.from;
              const isLast = !next || next.from !== msg.from;
              return (
                <MessageBubble key={i} msg={msg} isFirst={isFirst} isLast={isLast} />
              );
            })}
            {typing && (
              <div className="flex mb-1.5 animate-slide-up">
                <div className="mr-2 mt-auto">
                  <FlowAvatar size="w-7 h-7" />
                </div>
                <TypingIndicator />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick replies */}
          {quickReplies.length > 0 && !typing && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5 bg-[#FAFBFF] border-t border-[#F3F4F6] pt-2">
              {quickReplies.map((reply, i) => (
                <button
                  key={i}
                  onClick={() => handleQuickReply(reply)}
                  className="px-3 py-1.5 bg-white text-[#2A59C6] border border-[#E5E7EB] hover:border-[#2A59C6] hover:bg-[#F0F4FF] rounded-full text-xs font-medium transition"
                >
                  {reply}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="border-t border-[#E5E7EB] px-3 py-2.5 bg-white flex-shrink-0 flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSend();
              }}
              placeholder="Escríbele a Flow..."
              className="flex-1 px-3 py-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-full text-sm outline-none focus:border-[#2A59C6] focus:bg-white transition"
            />
            <button
              onClick={() => handleSend()}
              disabled={!input.trim()}
              className="w-9 h-9 rounded-full flex items-center justify-center bg-[#2A59C6] text-white disabled:bg-[#E5E7EB] disabled:text-[#9CA3AF] hover:bg-[#1E4BB8] transition flex-shrink-0"
              aria-label="Enviar"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
